/** A small LSP client for pyright, over the gateway's WebSocket.
 *
 * Hand-rolled rather than `monaco-languageclient`, which needs shims pinned to a
 * matching Monaco build and breaks on upgrades. We use four features —
 * diagnostics, completion, hover, signature help — so the glue is smaller than the
 * dependency.
 *
 * The editor must stay usable when this fails: no language server means no
 * completions, never a broken editor. Every entry point resolves empty on error.
 */

export interface Position {
  line: number; // 0-based, unlike Monaco
  character: number;
}

export interface Diagnostic {
  range: { start: Position; end: Position };
  severity?: 1 | 2 | 3 | 4;
  message: string;
  source?: string;
}

type Pending = { resolve: (value: unknown) => void; reject: (reason: unknown) => void };

export const WORKSPACE_URI = "file:///workspace";

export function uriFor(path: string): string {
  return `${WORKSPACE_URI}/${path.replace(/^\/+/, "")}`;
}

export class LspClient {
  private socket: WebSocket | null = null;
  private nextId = 1;
  private pending = new Map<number, Pending>();
  private versions = new Map<string, number>();
  private queue: string[] = [];
  private ready = false;
  /** Open documents and their current text, so a reconnect can re-announce them.
   *  A fresh server knows nothing; without the replay the editor keeps its old
   *  squiggles and offers no completions, with no clue why. */
  private docs = new Map<string, string>();

  onDiagnostics: (uri: string, diagnostics: Diagnostic[]) => void = () => {};
  onStatus: (up: boolean) => void = () => {};

  /** Connect, and resolve when the connection *ends*.
   *
   * Resolving on open instead makes a retry loop fire against a healthy socket:
   * each pass opens another connection, and every connection costs a language
   * server process in the candidate's container. That leaked ~40 of them and
   *2 GB before anyone looked.
   */
  async start(url: string): Promise<void> {
    // Never run two at once, whatever the caller does.
    this.stop();
    return new Promise((resolve) => {
      const socket = new WebSocket(url);
      this.socket = socket;

      socket.onopen = async () => {
        await this.request("initialize", {
          processId: null,
          rootUri: WORKSPACE_URI,
          workspaceFolders: [{ uri: WORKSPACE_URI, name: "workspace" }],
          capabilities: {
            textDocument: {
              synchronization: { dynamicRegistration: false },
              completion: { completionItem: { snippetSupport: false } },
              hover: { contentFormat: ["markdown", "plaintext"] },
              publishDiagnostics: {},
            },
          },
        }).catch(() => undefined);
        this.notify("initialized", {});
        this.ready = true;
        this.onStatus(true);
        for (const queued of this.queue.splice(0)) socket.send(queued);
        // Re-announce what the editor has open. The server is new and empty.
        for (const [path, text] of [...this.docs]) {
          this.versions.delete(uriFor(path));
          this.didOpen(path, text);
        }
      };

      socket.onmessage = (event) => this.receive(String(event.data));
      socket.onclose = () => {
        this.ready = false;
        this.onStatus(false);
        for (const { reject } of this.pending.values()) reject(new Error("language server closed"));
        this.pending.clear();
        resolve(); // the connection is over; the caller may now retry
      };
      socket.onerror = () => {
        // onclose follows an error, so resolving here too would double-resolve
        // and start a second retry timer. Leave it to onclose.
      };
    });
  }

  private receive(raw: string): void {
    let message: Record<string, unknown>;
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }
    if (message.method === "textDocument/publishDiagnostics") {
      const params = message.params as { uri: string; diagnostics: Diagnostic[] };
      this.onDiagnostics(params.uri, params.diagnostics ?? []);
      return;
    }
    if (typeof message.id === "number" && this.pending.has(message.id)) {
      const entry = this.pending.get(message.id)!;
      this.pending.delete(message.id);
      if (message.error) entry.reject(message.error);
      else entry.resolve(message.result);
      return;
    }
    // Server-to-client requests we do not implement must still be answered, or
    // pyright waits on them forever.
    if (typeof message.id === "number" && message.method) {
      this.send({ jsonrpc: "2.0", id: message.id, result: null });
    }
  }

  private send(payload: unknown): void {
    const text = JSON.stringify(payload);
    if (this.socket?.readyState === WebSocket.OPEN && (this.ready || "id" in (payload as object))) {
      this.socket.send(text);
    } else {
      this.queue.push(text);
    }
  }

  request<T>(method: string, params: unknown): Promise<T> {
    if (!this.socket || this.socket.readyState > WebSocket.OPEN) {
      return Promise.reject(new Error("no language server"));
    }
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.send({ jsonrpc: "2.0", id, method, params });
      // pyright can stall on a large first index; a hung promise would freeze the
      // completion widget rather than simply offering nothing.
      setTimeout(() => {
        if (this.pending.delete(id)) reject(new Error(`${method} timed out`));
      }, 5000);
    });
  }

  notify(method: string, params: unknown): void {
    this.send({ jsonrpc: "2.0", method, params });
  }

  didOpen(path: string, text: string, languageId = "python"): void {
    const uri = uriFor(path);
    this.docs.set(path, text);
    this.versions.set(uri, 1);
    this.notify("textDocument/didOpen", {
      textDocument: { uri, languageId, version: 1, text },
    });
  }

  didChange(path: string, text: string): void {
    const uri = uriFor(path);
    this.docs.set(path, text);
    const version = (this.versions.get(uri) ?? 1) + 1;
    this.versions.set(uri, version);
    // Full-document sync: incremental would be faster, but a desynced incremental
    // stream produces confidently wrong diagnostics, which is worse than slower.
    this.notify("textDocument/didChange", {
      textDocument: { uri, version },
      contentChanges: [{ text }],
    });
  }

  didClose(path: string): void {
    const uri = uriFor(path);
    this.docs.delete(path);
    this.versions.delete(uri);
    this.notify("textDocument/didClose", { textDocument: { uri } });
  }

  stop(): void {
    try {
      this.socket?.close();
    } catch {
      /* already gone */
    }
  }
}
